import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDocument, usePDF } from './hooks';
import { PdfRender } from './PdfRender';
import styles from './styles.module.css';
import { useResizeDetector } from 'react-resize-detector';
import { usePreviewContext } from './context';
import { generatePdfFilename } from '../../utils';

export default function Preview() {
  const { id, source } = useParams();
  const { data: documentMetadata } = useDocument(id, source);
  const { setPreviewData } = usePreviewContext();
  const [downloadUrl, setDownloadUrl] = useState<string | undefined>();
  const [downloadFilename, setDownloadFilename] = useState<string>(
    `${id ?? 'document'}.pdf`
  );

  const {
    data: pdfBlob,
    isLoading,
    error,
    isError: isErrorFetchingPDF,
  } = usePDF(id, source);

  const { width, ref } = useResizeDetector({
    refreshMode: 'debounce',
    refreshRate: 128,
    handleHeight: false,
    handleWidth: true,
  });

  useEffect(() => {
    if (pdfBlob) {
      const blob = new Blob([pdfBlob], { type: 'application/pdf' });
      const objectUrl = URL.createObjectURL(blob);
      setDownloadUrl(objectUrl);

      let filename = `${id ?? 'document'}.pdf`;
      if (documentMetadata) {
        filename = generatePdfFilename({
          id,
          name: documentMetadata.name,
          contractNumbers: documentMetadata.contractNumbers,
          date: documentMetadata.date,
        });
      }
      setDownloadFilename(filename);

      return () => URL.revokeObjectURL(objectUrl);
    }
  }, [id, pdfBlob, documentMetadata]);

  const downloadTrackId =
    source === 'MAIN'
      ? 'button-download|Last ned Main'
      : 'button-download|Last ned Bank';

  useEffect(() => {
    setPreviewData({
      downloadUrl,
      downloadFilename,
      downloadTrackId,
      isErrorFetchingPDF,
    });

    return () => {
      setPreviewData({
        downloadUrl: undefined,
        downloadFilename: undefined,
        downloadTrackId: undefined,
        isErrorFetchingPDF: undefined,
      });
    };
  }, [
    downloadUrl,
    downloadFilename,
    downloadTrackId,
    isErrorFetchingPDF,
    setPreviewData,
  ]);

  return (
    <div className={styles.pdfBackground}>
      <div className={styles.pdfContainer} ref={ref}>
        <PdfRender
          isLoading={isLoading}
          error={error}
          pdfBlob={pdfBlob}
          pageWidth={width}
        />
      </div>
    </div>
  );
}
